/* eslint-disable unicorn/prevent-abbreviations */
import { RequirementWorkflowType } from "@clipboard-health/contract-worker-app-bff";
import {
  PageHeaderWrapper,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { DocumentStatus } from "@src/appV2/Accounts/Documents/types";
import { FullScreenDialog } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { DocumentSubmissionErrorBottomSheet } from "@src/appV2/redesign/Documents/Submission/ErrorBottomSheet";
import { SHIFT_DISCOVERY_SHIFT_DOCUMENT_SUBMITTED_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { type HelloSignModalRoutePathParams } from "@src/appV2/redesign/ShiftDiscovery/types";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";

import { PageHeaderTopRow } from "../../../../components/PageHeaderTopRow";
import { PandaDocSigningContent } from "../../../../Documents/Details/Workflows/PandaDoc/PandaDocSigningContent";
import { usePandaDocSigningSession } from "../../../../Documents/Details/Workflows/PandaDoc/usePandaDocSigningSession";
import { DocumentSubmissionInProgressBottomSheet } from "../../../../Documents/Submission/InProgressBottomSheet";

interface InstantReviewPandaDocFormBottomSheetProps {
  modalState: UseModalState;
}

export function InstantReviewPandaDocFormBottomSheet(
  props: InstantReviewPandaDocFormBottomSheetProps
) {
  const { modalState } = props;
  const submittingDocumentModalState = useModalState();
  const errorModalState = useModalState();
  const { navigateToModal } = useShiftModalsDataContext();
  const { shiftId, requirementId } = useParams<HelloSignModalRoutePathParams>();

  const {
    signableESignWorkflowSession,
    isLoadingPandaDoc,
    startSigning,
    handleDocumentLoaded,
    completeSigningAndGetDocumentStatus,
  } = usePandaDocSigningSession({
    requirementId,
    signType: "shiftBookingFlow",
    onSessionLoadError: useCallback(
      (error: unknown) => {
        logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_FAILED, {
          shiftId: shiftId ?? "",
          requirementId,
          requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
        });
        logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
          error,
          metadata: { requirementId, shiftId },
        });
        errorModalState.openModal();
      },
      [errorModalState, shiftId, requirementId]
    ),
  });

  const onPandaDocCompleted = useCallback(async () => {
    submittingDocumentModalState.openModal();
    let documentStatus: DocumentStatus | undefined;
    try {
      documentStatus = await completeSigningAndGetDocumentStatus();
    } catch (error: unknown) {
      submittingDocumentModalState.closeModal();
      errorModalState.openModal();
      logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
        error,
        metadata: { requirementId, shiftId },
      });
      logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_FAILED, {
        shiftId: shiftId ?? "",
        requirementId,
        requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
      });
      return;
    }

    logEvent(APP_V2_USER_EVENTS.PANDADOC_SDK_DOCUMENT_SIGNED, {
      requirementId,
      signType: "shiftBookingFlow",
    });

    logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_SUCCESSFUL, {
      shiftId: shiftId ?? "",
      requirementId,
      documentStatus: documentStatus ?? DocumentStatus.PENDING,
      requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
    });
    submittingDocumentModalState.closeModal();
    navigateToModal(SHIFT_DISCOVERY_SHIFT_DOCUMENT_SUBMITTED_MODAL_PATH, {
      pathParams: {
        shiftId,
        requirementId,
        documentStatus: documentStatus ?? DocumentStatus.PENDING,
      },
      forceReplacePath: true,
    });
  }, [
    completeSigningAndGetDocumentStatus,
    navigateToModal,
    submittingDocumentModalState,
    errorModalState,
    shiftId,
    requirementId,
  ]);

  useEffect(() => {
    if (requirementId) {
      startSigning();
    }
  }, [requirementId, startSigning]);

  return (
    <>
      <FullScreenDialog skipTopSafeAreaPadding variant="tertiary" modalState={modalState}>
        <PageHeaderWrapper variant="tertiary">
          <PageHeaderTopRow
            title="Sign Document"
            onClickBack={() => {
              modalState.closeModal();
            }}
          />
        </PageHeaderWrapper>
        <PandaDocSigningContent
          isLoading={isLoadingPandaDoc}
          pandaDocSessionId={signableESignWorkflowSession?.attributes.esignProviderData?.sessionId}
          onDocumentLoaded={handleDocumentLoaded}
          onError={() => {
            logEvent(APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_FAILED, {
              shiftId: shiftId ?? "",
              requirementId,
              requirementWorkflowType: RequirementWorkflowType.SIGN_WITH_HELLOSIGN,
            });
            logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
              error: new Error("PandaDoc Embedded Signing SDK Error"),
              metadata: { requirementId, shiftId },
            });
            errorModalState.openModal();
          }}
          onComplete={onPandaDocCompleted}
        />

        <DocumentSubmissionInProgressBottomSheet modalState={submittingDocumentModalState} />
      </FullScreenDialog>

      <DocumentSubmissionErrorBottomSheet
        modalState={errorModalState}
        onClick={() => {
          errorModalState.closeModal();
          startSigning();
        }}
      />
    </>
  );
}
/* eslint-enable unicorn/prevent-abbreviations */
